import type { VideoBlockAbstract } from '@lighthouse/core'
import {
    flexMinSizeStyle,
    getCursorStyle,
    getImageFullUrlInApplication,
    PAGE_SCROLL_PARENT_CONTENT,
    spaceGradeVideoMaxQualityMap,
    useApplicationContext,
    VIDEO_RATIO_OPTIONS,
    VideoPlayer
} from '@lighthouse/shared'
import { find } from 'rambda'
import React, { memo, useMemo } from 'react'
import styled from 'styled-components'

import { AnimationDecorators } from '../../components'
import { useEffectiveAnchorId } from '../../context'
import { useEnterVisibleArea } from '../../hooks/useEnterVisibleArea'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'

interface VideoBlockProps {
    blockData: VideoBlockAbstract
    value: string
    cover?: string
    disabled?: boolean
}

const SCxContainer = styled(AnimationDecorators)<Pick<React.CSSProperties, 'borderRadius'>>`
    /* padding: var(--block-padding); */
    height: auto;
    width: 100%;
    overflow: hidden;
    > div {
        border-radius: ${({ borderRadius }) => borderRadius};
    }
`

const VideoBlock: React.FunctionComponent<VideoBlockProps> = ({ blockData, value, cover }) => {
    const { config, id, title } = blockData
    const {
        isMute,
        isLoop,
        resumePlay,
        isUsedVideoControl,
        radius,
        ratio,
        fitType,
        isAuto,
        showLoading,
        breakPoint,
        cursor,
        anchor,
        isVideoQualityLimit
    } = config
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)

    const { appId = '', isMobile, spaceVersion } = useApplicationContext()
    const maxQuality = spaceVersion ? spaceGradeVideoMaxQualityMap[spaceVersion] : undefined

    const { ref, isIn } = useEnterVisibleArea(PAGE_SCROLL_PARENT_CONTENT)

    const alignItem = useMemo(() => find(item => item.value === ratio, VIDEO_RATIO_OPTIONS), [ratio])

    return (
        <SCxContainer
            ref={ref}
            borderRadius={`${radius}px`}
            blockId={id}
            blockName={title}
            animation={breakPoint.animation}
            id={effectiveAnchorId}
            data-stop-action-propagation={isUsedVideoControl ? true : undefined}
            style={{ ...flexMinSizeStyle, ...getCursorStyle(appId, cursor) }}
        >
            <VideoPlayer
                ratio={alignItem?.label}
                source={value}
                isMute={isMute}
                resumePlay={resumePlay}
                isLoop={isLoop}
                maxQuality={maxQuality}
                isOrigin={!isVideoQualityLimit}
                cover={getImageFullUrlInApplication(appId, cover)}
                isController={isUsedVideoControl}
                fitType={fitType}
                autoplay={!isMobile ? isAuto && isIn : isAuto}
                showLoading={showLoading}
            />
        </SCxContainer>
    )
}

export default memo(VideoBlock)
